// components/Tooltip.jsx
"use client";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { cloneElement, isValidElement, ReactNode, useState } from "react";

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: "top" | "bottom" | "left" | "right";
  offsetValue?: number;
  animationClass?: string;
}

export function Tooltip({
  children,
  content,
  placement = "top",
  offsetValue = 0,
  animationClass = "transition-opacity duration-200 ease-out",
}: TooltipProps) {
  const [open, setOpen] = useState(false);

  const {
    x,
    y,
    strategy,
    context,
    refs: { setReference, setFloating },
  } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [offset(offsetValue), shift(), flip()],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context);
  const focus = useFocus(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, role]);

  return (
    <>
      {isValidElement(children)
        ? cloneElement(
            children,
            getReferenceProps({
              ref: setReference,
            }),
          )
        : console.error("Tooltip children must be a valid ReactElement.")}
      {open && (
        <FloatingPortal>
          <div
            ref={setFloating}
            {...getFloatingProps({
              style: {
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              },
            })}
            className={`${animationClass} z-50`}
          >
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
